import { Alert, Box, Typography } from "@mui/material";
import type { DepartmentRolesUpdate, DuplicateDetail, LoadNursesResults } from "./NursesSummary";

type Props = {
	results: LoadNursesResults;
	dataTestId: string;
};

const hasRoleChanges = (update: DepartmentRolesUpdate | undefined): update is DepartmentRolesUpdate =>
	Boolean(update && (update.roles_added.length > 0 || update.default_role_set || update.role_groups_set));

function DuplicateGroup({ label, details }: { label: string; details: DuplicateDetail[] }) {
	if (details.length === 0) return null;
	return (
		<Box sx={{ mb: 1.5 }}>
			<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
				{label}
			</Typography>
			{details.map((detail) => (
				<Box key={detail.phone ?? detail.email} sx={{ ml: 2, mb: 1 }}>
					<Typography variant="body2">
						<strong>{detail.phone ?? detail.email}</strong>
						{` — appeared ${detail.total_count} time(s), cleared from:`}
					</Typography>
					{detail.occurrences.map((occurrence) => (
						<Typography key={`${occurrence.name}-${occurrence.index}`} variant="body2" sx={{ ml: 2, color: "text.secondary" }}>
							{`• ${occurrence.name} (row ${occurrence.index})`}
						</Typography>
					))}
				</Box>
			))}
		</Box>
	);
}

/**
 * The two side effects a nurses load has on things OTHER than the rows in the file: what it changed
 * about the department's role configuration, and whose contact details it cleared.
 *
 * Kept as its own panel rather than folded into `NursesSummary` because that component reports the
 * file's own rows — created/updated/failed. These are consequences the onboarder did not ask for and
 * cannot see anywhere else: a duplicate phone number is stripped from EVERY nurse that carries it,
 * including people who were already in the department and are nowhere in the uploaded file.
 */
export function NurseLoadSideEffects({ results, dataTestId }: Props) {
	const rolesUpdate = results.department_roles_update;
	const duplicateCleanup = results.duplicate_cleanup;
	const clearedCells = duplicateCleanup ? duplicateCleanup.duplicate_phones_cleaned + duplicateCleanup.duplicate_emails_cleaned : 0;

	return (
		<>
			{hasRoleChanges(rolesUpdate) && (
				<Alert severity="success" sx={{ mb: 2 }} data-testid={`${dataTestId}-roles-update`}>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
						Department roles config updated
					</Typography>
					{rolesUpdate.roles_added.length > 0 && <Typography variant="body2">{`Roles added: ${rolesUpdate.roles_added.join(", ")}`}</Typography>}
					{rolesUpdate.default_role_set && <Typography variant="body2">{`Default role set: ${rolesUpdate.default_role_set}`}</Typography>}
					{rolesUpdate.role_groups_set && (
						<Typography variant="body2">
							{`Role groups set: ${Object.entries(rolesUpdate.role_groups_set)
								.map(([group, roles]) => `${group}: [${roles.join(", ")}]`)
								.join(", ")}`}
						</Typography>
					)}
				</Alert>
			)}

			{clearedCells > 0 && duplicateCleanup && (
				<Alert severity="warning" sx={{ mb: 2 }} data-testid={`${dataTestId}-duplicate-cleanup`}>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
						Duplicate contacts removed
					</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>
						{`${duplicateCleanup.phone_details.length} duplicate phone number(s) and ${duplicateCleanup.email_details.length} duplicate email(s) were cleared from every nurse carrying them — ${clearedCells} cell(s) in total, including nurses who are not in this file.`}
					</Typography>
					<DuplicateGroup label="Phone numbers" details={duplicateCleanup.phone_details} />
					<DuplicateGroup label="Emails" details={duplicateCleanup.email_details} />
				</Alert>
			)}
		</>
	);
}
